import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { SelectField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { TabPanel, Tabs } from '../components/Tabs.tsx';
import { dateText, loadAllInvoices, moneyText, type Invoice } from './api.ts';
import { INVOICE_STATUS } from './AdminTenantDetail.tsx';
import { CostsPanel } from './CostsPanel.tsx';
import { ProfitLossPanel } from './ProfitLossPanel.tsx';
import { TableWrap } from '../components/TableWrap.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

type InvoicingTab = 'issued' | 'costs' | 'pnl';
const TABS: ReadonlyArray<readonly [InvoicingTab, string]> = [
  ['issued', 'Invoices issued'],
  ['costs', 'Costs'],
  ['pnl', 'Profit and loss'],
];

/**
 * Braynewave's Invoicing (R189; docs/specs/cloud/platform-costs.md §1): the invoices it issued, the costs it received
 * and the profit and loss of the two.
 */
export function AdminInvoicesScreen({ sb }: { readonly sb: SupabaseClient }): ReactElement {
  const [tab, setTab] = useState<InvoicingTab>('issued');
  return (
    <section className="page">
      <PageHeader
        title="Invoicing"
        subtitle="The invoices Braynewave sent each organisation, the costs it received, and the profit and loss."
      />
      <Tabs label="Invoicing sections" tabs={TABS} current={tab} onChange={setTab} idPrefix="invoicing" />
      <TabPanel idPrefix="invoicing" current={tab}>
        {tab === 'issued' ? (
          <IssuedPanel sb={sb} />
        ) : tab === 'costs' ? (
          <CostsPanel sb={sb} />
        ) : (
          <ProfitLossPanel sb={sb} />
        )}
      </TabPanel>
    </section>
  );
}

/** Every invoice Braynewave has recorded, across organisations; recorded and changed on each organisation's page. */
function IssuedPanel({ sb }: { readonly sb: SupabaseClient }): ReactElement {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState<'all' | Invoice['status']>('issued');

  const reload = useCallback(() => {
    setError(null);
    loadAllInvoices(sb)
      .then(setInvoices)
      .catch((e: unknown) => setError(message(e)));
  }, [sb]);
  useEffect(reload, [reload]);

  const shown = useMemo(
    () => (invoices ?? []).filter((i) => show === 'all' || i.status === show),
    [invoices, show],
  );
  /** Not paid yet, per currency. */
  const owing = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of invoices ?? [])
      if (i.status === 'issued') m.set(i.currency, (m.get(i.currency) ?? 0) + i.amount);
    return [...m.entries()];
  }, [invoices]);

  return (
    <div className="stack">
      <p className="caption muted">
        Invoices Braynewave sent by hand. Record and change them on each organisation's page (Tenants).
      </p>
      {error && <ErrorState message={error} onRetry={reload} />}
      {!invoices && !error && <Loading what="invoices" />}
      {invoices && (
        <>
          <p className="body">
            Not paid yet: {owing.length === 0 ? 'nothing' : owing.map(([c, a]) => moneyText(a, c)).join(', ')}
            .
          </p>
          <div className="form-grid">
            <SelectField
              label="Show"
              value={show}
              onChange={setShow}
              options={[
                ['issued', 'Not paid yet'],
                ['paid', 'Paid'],
                ['void', 'Void'],
                ['all', 'All invoices'],
              ]}
            />
          </div>
          <TableWrap>
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Organisation</th>
                  <th className="label">Invoice</th>
                  <th className="label">Issued</th>
                  <th className="label">Amount</th>
                  <th className="label">GST included</th>
                  <th className="label">Paid</th>
                  <th className="label">Notes</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      No invoices here.
                    </td>
                  </tr>
                )}
                {shown.map((i) => (
                  <tr key={i.id}>
                    <td>{i.tenantName ?? '–'}</td>
                    <td className="data">{i.ref}</td>
                    <td>{dateText(i.issuedOn)}</td>
                    <td className="data">{moneyText(i.amount, i.currency)}</td>
                    <td className="data">{moneyText(i.gst, i.currency)}</td>
                    <td>{i.status === 'paid' ? `Paid ${dateText(i.paidOn)}` : INVOICE_STATUS[i.status]}</td>
                    <td className="caption">{i.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}
    </div>
  );
}
